import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useCameraKit } from "../hooks/useCameraKit";
import {
  CameraKitSource,
  Transform2D,
  createMediaStreamSource,
} from "@snap/camera-kit";
import Loading from "../components/Loading/Loading";

export interface CameraInfo {
  deviceId: string;
  label: string;
}

interface CameraSourceState {
  devices: CameraInfo[] | null;
  selectedDeviceId: string;
  source?: CameraKitSource;
  setDevice: (options: DeviceOptions) => Promise<void>;
  isLoading: boolean;
  isCameraAccessGranted: boolean;
  mediaStream?: MediaStream;
  currentDimension: Dimension;
  maxDimension: number;
  hasVideoFeed: boolean;
}

interface CameraSourceProps {
  initialDeviceId?: string;
  fpsLimit?: number | undefined;
  children?: ReactNode;
  onCameraPermissionError?: (granted: boolean) => void;
}

interface DimensionOptions {
  label: string;
  width: number;
  height: number;
}

interface DeviceOptions {
  deviceId?: string;
  dimension?: Dimension;
  facingMode?: "environment" | "user";
}

const MAXIMUM_LOADING_TIME_MS = 10000; 

export const ALLOWED_DIMENSIONS: Record<number, DimensionOptions> = {
  2160: {
    label: "4K",
    width: 3840,
    height: 2160,
  },
  1440: {
    label: "1440p",
    width: 2560,
    height: 1440,
  },
  1080: {
    label: "1080p",
    width: 1920,
    height: 1080,
  },
  720: {
    label: "720p",
    width: 1280,
    height: 720,
  },
  480: {
    label: "480p",
    width: 854,
    height: 480,
  },
  360: {
    label: "360p",
    width: 640,
    height: 360,
  },
} as const;

type Dimension = keyof typeof ALLOWED_DIMENSIONS;

export enum FacingMode {
  ENVIRONMENT = "environment",
  USER = "user",
}

export const CameraSourceMasterContext = createContext<
  CameraSourceState | undefined
>(undefined);

export const CameraSourceContext: React.FC<CameraSourceProps> = ({
  initialDeviceId,
  fpsLimit,
  children,
  onCameraPermissionError,
}) => {
  const isMounted = useRef<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { session } = useCameraKit();
  const [mediaStream, setMediaStream] = useState<MediaStream>();
  const [source, setSource] = useState<CameraKitSource>();
  const [devices, setDevices] = useState<CameraInfo[] | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCameraAccessGranted, setIsCameraAccessGranted] = useState<boolean>(false);
  const [hasVideoFeed, setHasVideoFeed] = useState<boolean>(false);
  const [currentDimension, setCurrentDimension] = useState<Dimension>(720);
  const [maxDimension, setMaxDimension] = useState<number>(0);
  const [currentFacingMode, setCurrentFacingMode] = useState<FacingMode>(
    FacingMode.ENVIRONMENT
  );

  // Clear timeout when component unmounts or when camera access is successful
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Monitor video feed status
  useEffect(() => {
    if (mediaStream) {
      const videoTrack = mediaStream.getVideoTracks()[0];
      if (videoTrack && videoTrack.readyState === 'live') {
        setHasVideoFeed(true);
        // Clear any pending timeout since video feed is working
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    } else {
      setHasVideoFeed(false);
    }
  }, [mediaStream]);

  const setDevice = useCallback(
    async ({
      deviceId = selectedDeviceId,
      dimension = currentDimension,
      facingMode = currentFacingMode,
    }: DeviceOptions) => {
      if (isLoading) return;

      setIsLoading(true);
      setHasVideoFeed(false);

      // Set timeout for hanging getUserMedia
      timeoutRef.current = setTimeout(() => {
        if (!hasVideoFeed && !isCameraAccessGranted) {
          setIsLoading(false);
          onCameraPermissionError?.(true);
        }
      }, MAXIMUM_LOADING_TIME_MS);

      const { width, height } = ALLOWED_DIMENSIONS[dimension];

      try {
        if (mediaStream) {
          mediaStream.getVideoTracks()[0].stop();
        }
        const newMediaStream = await navigator.mediaDevices
          .getUserMedia({
            video: {
              width,
              height,
              facingMode,
            },
            audio: false,
          });

        if (!newMediaStream) {
          // Clear timeout and trigger error
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          onCameraPermissionError?.(true);
          setIsLoading(false);
          return;
        }

        const track = newMediaStream.getVideoTracks()[0];
        const source = createMediaStreamSource(newMediaStream, {
          cameraType: facingMode,
          fpsLimit,
        });

        await session.setSource(source);

        if (facingMode === "user") {
          source.setTransform(Transform2D.MirrorX);
        } else {
          source.setTransform(Transform2D.Identity);
        }

        setMediaStream(newMediaStream);
        setSource(source);
        setCurrentDimension(dimension);
        setSelectedDeviceId(track.getSettings().deviceId ?? "");
        setMaxDimension(((track.getCapabilities()?.width?.max ?? 0) * 9) / 16);
        setIsCameraAccessGranted(true);
        setHasVideoFeed(true);
        
        // Clear timeout since everything is working
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        setIsLoading(false);
      } catch (error: any) {
        console.error(error);
        // Clear timeout and trigger error
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        onCameraPermissionError?.(true);
        setIsLoading(false);
        setIsCameraAccessGranted(false);
        setHasVideoFeed(false);
      }
    },
    [
      selectedDeviceId,
      currentDimension,
      currentFacingMode,
      isLoading,
      hasVideoFeed,
      isCameraAccessGranted,
      session,
      mediaStream,
      fpsLimit,
      onCameraPermissionError,
    ]
  );

  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;

    async function initialize() {
      await setDevice({ deviceId: initialDeviceId });

      const devices = await navigator.mediaDevices.enumerateDevices();

      const cameraDevices = devices
        .filter((device) => device.kind === "videoinput")
        .map((device) => ({ deviceId: device.deviceId, label: device.label }));

      setDevices(cameraDevices);
    }

    initialize();
  }, [initialDeviceId, setDevice]);

  return (
    <>
      {isLoading ? (
        <Loading/>
      ) : (
        <></>
      )}
      <CameraSourceMasterContext.Provider
        value={{
          devices,
          source,
          selectedDeviceId,
          setDevice,
          isLoading,
          isCameraAccessGranted,
          mediaStream,
          currentDimension,
          maxDimension,
          hasVideoFeed,
        }}
      >
        {children}
      </CameraSourceMasterContext.Provider>
    </>
  );
};
